import { enforceHourMinuteString } from '@/modules/activities/util'

interface DistributionNamed {
  id: string
  name: string
}

/**
 * One block of activities the salary calculation grouped together: a stretch of
 * a shift on one vehicle under one salary code, carrying the department and
 * facility that stretch is attributed to. `department` / `facility` are null
 * when neither the activities nor the vehicle named one.
 */
export interface DistributionGroupedActivity {
  salaryId?: string | null
  salaryName?: string | null
  regNr?: string | null
  vehicleVin?: string | null
  startTime: string
  endTime: string
  timeWorkTotal?: string | null
  department?: DistributionNamed | null
  facility?: DistributionNamed | null
}

export interface DistributionWorkPeriodGroup {
  workPeriods: readonly {
    groupedActivities: readonly DistributionGroupedActivity[]
  }[]
}

export interface GroupedActivityRow {
  id: string
  /** ISO timestamps; the popup formats them for display. */
  start: string
  end: string
  salaryCode: string
  department: string
  departmentId: string
  facility: string
  facilityId: string
  vehicle: string
  amount: string
}

export interface GroupedActivityFilters {
  /** A department's relay id, or '' for every department. */
  department: string
  facility: string
}

/**
 * Flatten the month's work period groups into one row per grouped activity,
 * oldest first.
 *
 * Only the blocks carrying a salary code are kept: those are the ones that
 * reach payroll, and the popup exists to explain the salary code table. A
 * missing department or facility stays empty rather than being labelled, so a
 * gap in the attribution reads as a gap.
 */
export const buildGroupedActivityRows = (
  workPeriodGroups: readonly DistributionWorkPeriodGroup[]
): GroupedActivityRow[] => {
  const rows = workPeriodGroups.flatMap(group =>
    group.workPeriods.flatMap(workPeriod =>
      workPeriod.groupedActivities
        .filter(activity => !!activity.salaryId)
        .map((activity, index) => ({
          id: `${activity.startTime}-${activity.salaryId ?? ''}-${index}`,
          start: activity.startTime,
          end: activity.endTime,
          salaryCode: activity.salaryName ?? activity.salaryId ?? '',
          department: activity.department?.name ?? '',
          departmentId: activity.department?.id ?? '',
          facility: activity.facility?.name ?? '',
          facilityId: activity.facility?.id ?? '',
          vehicle: activity.regNr ?? activity.vehicleVin ?? '',
          amount: enforceHourMinuteString(activity.timeWorkTotal),
        }))
    )
  )

  return rows.sort((a, b) => a.start.localeCompare(b.start))
}

/**
 * The combobox value standing for "the rows with none at all".
 *
 * Blocks with no department or facility are the ones worth finding -- they are
 * the gaps in the attribution -- but they have no id to select, so both
 * comboboxes carry this entry alongside the company's own rows. It cannot
 * collide with a relay id, which is always base64.
 */
export const UNASSIGNED = '__unassigned__'

function matches(selected: string, rowId: string) {
  if (!selected) return true
  if (selected === UNASSIGNED) return !rowId
  return rowId === selected
}

/**
 * Narrow the rows to one department and/or facility. Both comboboxes are
 * cleared to '' for "all", take `UNASSIGNED` for the rows carrying none, and
 * apply together.
 */
export const filterGroupedActivityRows = (
  rows: readonly GroupedActivityRow[],
  { department, facility }: GroupedActivityFilters
): GroupedActivityRow[] =>
  rows.filter(
    row =>
      matches(department, row.departmentId) && matches(facility, row.facilityId)
  )
